import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Save, Search, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import ConfirmModal from '@/components/ui/ConfirmModal';

const CrudTable = ({ tableName, columns, title, icon: Icon }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [newItem, setNewItem] = useState({});
    const [selectOptions, setSelectOptions] = useState({});
    const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });
    const { toast } = useToast();

    // Columnas a mostrar (excluyendo id, created_at, etc si no se piden)
    const displayColumns = columns.filter(col => !col.hidden);

    useEffect(() => {
        const fetchSelectOptions = async () => {
            const selectCols = columns.filter(col => col.type === 'select' && col.optionsTable);
            if (selectCols.length === 0) return;

            const options = {};
            await Promise.all(selectCols.map(async (col) => {
                const { data } = await supabase.from(col.optionsTable).select('*').order('id');
                options[col.key] = data || [];
            }));
            setSelectOptions(options);
        };

        fetchSelectOptions();
        fetchData();
    }, [tableName, columns]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const { data: result, error } = await supabase
                .from(tableName)
                .select('*')
                .order('id', { ascending: true });

            if (error) throw error;
            setData(result);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast({ title: 'Error', description: 'No se pudieron cargar los datos', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id) => {
        setConfirmConfig({
            isOpen: true,
            title: `¿Eliminar Registro?`,
            message: `¿Estás seguro de que deseas eliminar este registro de la tabla "${tableName}"? Esta acción no se puede deshacer.`,
            onConfirm: () => executeDelete(id)
        });
    };

    const executeDelete = async (id) => {
        try {
            const { error } = await supabase.from(tableName).delete().eq('id', id);
            if (error) throw error;

            setData(data.filter(item => item.id !== id));
            toast({ title: 'Eliminado', description: 'Registro eliminado correctamente', className: 'bg-green-600 text-white border-0' });
        } catch (error) {
            console.error('Error deleting:', error);
            toast({ title: 'Error', description: 'No se pudo eliminar. Puede estar en uso.', variant: 'destructive' });
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const isEditing = !!editingItem;
        const payload = isEditing ? editingItem : newItem;

        try {
            let result;
            if (isEditing) {
                // Filtrar columnas que no deben enviarse en un UPDATE (como ID o created_at)
                const { id, created_at, ...updatePayload } = payload;

                const { data: updated, error } = await supabase
                    .from(tableName)
                    .update(updatePayload)
                    .eq('id', id)
                    .select();
                if (error) throw error;
                result = updated[0];
                setData(data.map(item => (item.id === result.id ? result : item)));
            } else {
                const { data: created, error } = await supabase
                    .from(tableName)
                    .insert(payload)
                    .select();
                if (error) throw error;
                result = created[0];
                setData([...data, result]);
            }

            toast({
                title: 'Guardado',
                description: `Registro ${isEditing ? 'actualizado' : 'creado'} correctamente`,
                className: 'bg-green-600 text-white border-0'
            });
            closeModal();
        } catch (error) {
            console.error('Error saving:', error);
            toast({ title: 'Error', description: error.message || 'Error al guardar', variant: 'destructive' });
        }
    };

    const openModal = (item = null) => {
        if (item) {
            setEditingItem(item);
        } else {
            setEditingItem(null);
            setNewItem({});
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setNewItem({});
    };

    const handleInputChange = (field, value) => {
        if (editingItem) {
            setEditingItem({ ...editingItem, [field]: value });
        } else {
            setNewItem({ ...newItem, [field]: value });
        }
    };

    const filteredData = data.filter(item =>
        displayColumns.some(col =>
            String(item[col.key] || '').toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                        {Icon && <Icon className="text-red-600" />}
                        {title}
                    </h2>
                    <p className="text-zinc-500 text-sm">Gestiona el catálogo de {title.toLowerCase()}</p>
                </div>
                <button
                    onClick={() => openModal()}
                    className="w-full sm:w-auto bg-red-600 hover:bg-red-500 text-white px-6 py-3 sm:py-2 rounded-xl sm:rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-colors shadow-lg shadow-red-900/20 active:scale-95"
                >
                    <Plus size={18} /> Nuevo Registro
                </button>
            </div>

            {/* Search & Filters */}
            <div className="bg-white dark:bg-zinc-900/50 p-4 rounded-lg border border-gray-200 dark:border-zinc-800 flex items-center gap-3 shadow-sm dark:shadow-none transition-colors">
                <Search className="text-zinc-400" size={20} />
                <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-transparent border-none focus:outline-none text-zinc-900 dark:text-white w-full placeholder-zinc-400"
                />
            </div>

            {/* Table Container with Horizontal Scroll */}
            <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-xl overflow-hidden shadow-sm dark:shadow-xl transition-colors">
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left min-w-[600px]">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-black/40 border-b border-gray-200 dark:border-zinc-800 transition-colors">
                                {displayColumns.map((col) => (
                                    <th key={col.key} className="p-4 text-xs font-bold text-zinc-500 uppercase tracking-wider">
                                        {col.label}
                                    </th>
                                ))}
                                <th className="p-4 text-right text-xs font-bold text-zinc-500 uppercase tracking-wider">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-zinc-800/50">
                            <AnimatePresence>
                                {loading ? (
                                    <tr><td colSpan="100" className="p-8 text-center text-zinc-500">Cargando datos...</td></tr>
                                ) : filteredData.length === 0 ? (
                                    <tr><td colSpan="100" className="p-8 text-center text-zinc-500">No se encontraron registros</td></tr>
                                ) : (
                                    filteredData.map((item) => (
                                        <motion.tr
                                            key={item.id}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="hover:bg-gray-50 dark:hover:bg-zinc-800/30 transition-colors group"
                                        >
                                            {displayColumns.map((col) => (
                                                <td key={col.key} className="p-4 text-zinc-700 dark:text-zinc-300 text-sm">
                                                    {col.type === 'color' ? (
                                                        <div className="flex items-center gap-2">
                                                            <div
                                                                className="w-4 h-4 rounded-full border border-gray-200 shadow-sm"
                                                                style={{ backgroundColor: item[col.key] || '#ccc' }}
                                                            />
                                                            <span className="font-mono uppercase">{item[col.key]}</span>
                                                        </div>
                                                    ) : col.type === 'select' ? (
                                                        <span className="font-medium text-zinc-900 dark:text-zinc-100">
                                                            {(selectOptions[col.key]?.find(opt => String(opt.id) === String(item[col.key]))?.[col.displayKey || 'nombre'] ||
                                                                selectOptions[col.key]?.find(opt => String(opt.id) === String(item[col.key]))?.categoria) ||
                                                                item[col.key]}
                                                        </span>
                                                    ) : col.render ? (
                                                        col.render(item[col.key], item)
                                                    ) : (
                                                        item[col.key]
                                                    )}
                                                </td>
                                            ))}
                                            <td className="p-4 text-right">
                                                <div className="flex items-center justify-end gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => openModal(item)}
                                                        className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-blue-600 dark:text-blue-400 rounded-md transition-colors"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(item.id)}
                                                        className="p-2 bg-gray-100 hover:bg-red-50 dark:bg-zinc-800 dark:hover:bg-red-900/50 text-red-600 dark:text-red-500 rounded-md transition-colors"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))
                                )}
                            </AnimatePresence>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeModal}
                            className="absolute inset-0 bg-black/50 dark:bg-black/80 backdrop-blur-sm transition-colors"
                        />
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative w-full max-w-lg bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden transition-colors flex flex-col max-h-[90vh]"
                        >
                            <div className="p-6 border-b border-gray-100 dark:border-zinc-800">
                                <h3 className="text-xl font-bold text-zinc-900 dark:text-white">
                                    {editingItem ? 'Editar Registro' : 'Nuevo Registro'}
                                </h3>
                            </div>

                            <form onSubmit={handleSave} className="flex flex-col overflow-hidden">
                                <div className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {displayColumns.map((col) => (
                                            <div key={col.key} className={col.type === 'color' ? 'sm:col-span-2' : ''}>
                                                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1 font-bold">
                                                    {col.label}
                                                </label>
                                                {col.type === 'boolean' ? (
                                                    <select
                                                        value={String((editingItem || newItem)[col.key] ?? true)}
                                                        onChange={(e) => handleInputChange(col.key, e.target.value === 'true')}
                                                        className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-zinc-700 rounded-lg px-3 py-2 text-zinc-900 dark:text-white focus:border-red-600 outline-none transition-colors"
                                                    >
                                                        <option value="true">Activo</option>
                                                        <option value="false">Inactivo</option>
                                                    </select>
                                                ) : col.type === 'color' ? (
                                                    <div className="flex items-center gap-3 bg-gray-50 dark:bg-black border border-gray-300 dark:border-zinc-700 rounded-lg px-3 py-2">
                                                        <input
                                                            type="color"
                                                            value={(editingItem || newItem)[col.key] || '#000000'}
                                                            onChange={(e) => handleInputChange(col.key, e.target.value)}
                                                            className="w-8 h-8 rounded cursor-pointer bg-transparent border-0"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={(editingItem || newItem)[col.key] || ''}
                                                            onChange={(e) => handleInputChange(col.key, e.target.value)}
                                                            className="flex-1 bg-transparent border-none text-zinc-900 dark:text-white focus:ring-0 outline-none font-mono text-sm uppercase"
                                                            placeholder="#000000"
                                                            maxLength={7}
                                                        />
                                                    </div>
                                                ) : col.type === 'select' ? (
                                                    <select
                                                        value={(editingItem || newItem)[col.key] || ''}
                                                        onChange={(e) => handleInputChange(col.key, e.target.value)}
                                                        className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-zinc-700 rounded-lg px-3 py-2 text-zinc-900 dark:text-white focus:border-red-600 outline-none transition-colors"
                                                        required={col.required}
                                                    >
                                                        <option value="">-- Seleccionar --</option>
                                                        {(selectOptions[col.key] || []).map(opt => (
                                                            <option key={opt.id} value={opt.id}>
                                                                {opt[col.displayKey] || opt.nombre || opt.categoria || opt.id}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        type={col.type || 'text'}
                                                        value={(editingItem || newItem)[col.key] || ''}
                                                        onChange={(e) => handleInputChange(col.key, e.target.value)}
                                                        className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-zinc-700 rounded-lg px-3 py-2 text-zinc-900 dark:text-white focus:border-red-600 outline-none transition-colors"
                                                        required={col.required}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="p-6 border-top border-gray-100 dark:border-zinc-800 flex gap-3 justify-end">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="px-4 py-2 rounded-lg text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white font-medium hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded-lg font-bold shadow-lg shadow-red-900/20 transition-all active:scale-95"
                                    >
                                        Guardar
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <ConfirmModal
                isOpen={confirmConfig.isOpen}
                onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
            />
        </div>
    );
};

export default CrudTable;
